<template>
    <div class="SidebarPanel__wrapper">
        <!-- Backdrop overlay -->
        <transition name="transition-fade">
            <button
                v-if="isOpen && (!persistent || showMobileBackdrop)"
                :class="['SidebarPanel__backdrop', { 'SidebarPanel__backdrop--visible': showMobileBackdrop }]"
                :aria-label="closeLabel"
                @click="$emit('close')"
            />
        </transition>

        <!-- Panel -->
        <div
            ref="focusTrapContainer"
            :dusk="dusk"
            :role="isOpen ? 'dialog' : undefined"
            :aria-modal="shouldTrapFocus ? 'true' : undefined"
            :aria-label="isOpen ? dialogLabel : undefined"
            :class="panelClasses"
            :style="panelStyle"
        >
            <!-- Drag handle for mobile bottom sheets -->
            <button class="SidebarPanel__handle" :aria-label="closeLabel" @click="$emit('close')">
                <span class="SidebarPanel__handle-line" />
            </button>

            <!-- Header -->
            <div v-if="!flush || isMobileFullscreen" class="flex shrink-0 flex-col gap-1 px-4 py-3">
                <div v-if="isMobileFullscreen" :class="['flex', side === 'left' ? 'justify-end' : '']">
                    <button
                        class="SidebarPanel__collapse"
                        :title="collapseTooltip"
                        :aria-label="closeLabel"
                        @click="$emit('close')"
                    >
                        <svg
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="2"
                            stroke="currentColor"
                            width="18"
                            height="18"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div v-if="!flush" class="flex items-center justify-between gap-2">
                    <span class="truncate text-[15px] font-semibold text-[var(--theme-text-color)]">
                        {{ title }}
                    </span>
                    <slot name="title-aside" />
                </div>
            </div>

            <!-- Toolbar slot (fixed, non-scrollable) -->
            <div v-if="$slots.toolbar" class="flex shrink-0 flex-col px-4">
                <slot name="toolbar" />
            </div>

            <!-- Content slot (scrollable) -->
            <div class="flex flex-1 flex-col overflow-y-auto" :class="{ 'p-4': !flush }">
                <slot />
            </div>

            <!-- Footer slot (fixed, non-scrollable) -->
            <div v-if="$slots.footer" class="SidebarPanel__footer">
                <slot name="footer" />
            </div>

            <!-- Desktop resize handle -->
            <div
                v-if="resizable && !onlyMobileVisible && !fullscreen"
                class="group absolute top-0 z-[51] h-full w-[5px] cursor-col-resize"
                :class="side === 'left' ? '-right-0.5' : '-left-0.5'"
                @pointerdown="startResize"
                @dblclick="resetWidth"
            >
                <span
                    class="absolute left-1/2 top-1/2 h-10 w-1 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-theme-toolbar-icon opacity-50 transition-opacity group-hover:opacity-100"
                    :class="{ 'opacity-100': isResizing }"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';
import { pushBackStep } from '../../js/utils/backSteps';
import { MIN_SIDEBAR_WIDTH } from '../../js/constants/sidebarLayout';

export default {
    name: 'SidebarPanel',
    mixins: [focusTrapMixin],

    props: {
        dusk: {
            type: String,
            required: false,
            default: null,
        },

        /**
         * Controls panel visibility.
         */
        isOpen: {
            type: Boolean,
            default: false,
        },
        /**
         * Header title text.
         */
        title: {
            type: String,
            default: '',
        },
        /**
         * Accessible dialog name when it must differ from the visible title.
         */
        accessibleLabel: {
            type: String,
            default: '',
        },
        /**
         * Panel position: 'left' or 'right'.
         */
        side: {
            type: String,
            default: 'right',
            validator: value => ['left', 'right'].includes(value),
        },
        /**
         * Expands panel width from 380px to 480px.
         */
        expanded: {
            type: Boolean,
            default: false,
        },
        /**
         * Full viewport mode (100vw x 100vh), hides header/toolbar/footer.
         */
        fullscreen: {
            type: Boolean,
            default: false,
        },
        /**
         * Keeps the footer on screen in fullscreen, where it otherwise hides
         * with the header and toolbar.
         */
        fullscreenFooter: {
            type: Boolean,
            default: false,
        },
        /**
         * When true, hides backdrop allowing interaction with reader content.
         */
        persistent: {
            type: Boolean,
            default: false,
        },
        /**
         * While open, keeps the keyboard inside (Tab cycles, Escape closes)
         * and tells assistive tech the rest of the reader is out of reach.
         * Off, the panel is still a dialog, and the page beside it has the keyboard.
         */
        modal: {
            type: Boolean,
            default: true,
        },
        /**
         * Selector of the control that gets the focus back on close when the
         * one that opened the panel is no longer in the DOM.
         */
        returnFocusSelector: {
            type: String,
            default: null,
        },
        /**
         * Mobile-only fullscreen mode (100vh, no rounded corners).
         */
        mobileFullscreen: {
            type: Boolean,
            default: false,
        },
        /**
         * CSS height for partial mobile view (e.g., '48vh', '50vh').
         */
        peekHeight: {
            type: String,
            default: null,
        },
        /**
         * Shows a semi-transparent overlay backdrop on mobile (like bottom sheets).
         * Only applies when on mobile view.
         */
        mobileBackdrop: {
            type: Boolean,
            default: false,
        },
        /**
         * Enables the desktop drag-to-resize handle (left panels only).
         */
        resizable: {
            type: Boolean,
            default: false,
        },
        /**
         * Lets the content sit flush against the panel edges: drops the title row and the
         * content padding, for panels that paint their own header. `title` is still used
         * for the dialog's aria-label, and the mobile close button is kept.
         */
        flush: {
            type: Boolean,
            default: false,
        },
        focusTrapFallback: {
            type: String,
            default: 'first',
            validator: value => ['first', 'container'].includes(value),
        },
    },

    data() {
        return {
            focusTrapEnabled: false,
            // The panel answers back while open, trapped or not.
            focusTrapStepsBack: false,
            isResizing: false,
            stopActiveResize: null,
        };
    },

    computed: {
        ...mapState(['ui', 'localizations', 'lang']),
        ...mapGetters(['onlyMobileVisible']),

        shouldTrapFocus() {
            // TODO: review this when sidebar left is persistent
            // return !this.persistent && this.isOpen;
            return this.isOpen && this.modal;
        },

        // Losing `modal` is a pause, which on a phone also takes the sheet off
        // screen, so only a modal panel closing hands the focus back.
        focusTrapRestoresFocus() {
            return this.modal;
        },

        focusTrapReturnSelector() {
            return this.returnFocusSelector;
        },

        closeLabel() {
            return this.localizations?.[this.lang]?.tooltips?.close;
        },

        dialogLabel() {
            return this.accessibleLabel || this.title;
        },

        isMobileFullscreen() {
            return this.mobileFullscreen && this.onlyMobileVisible;
        },

        showMobileBackdrop() {
            return this.mobileBackdrop && this.onlyMobileVisible;
        },

        panelStyle() {
            if (this.fullscreen || this.isMobileFullscreen) {
                return '';
            }
            if (this.peekHeight && this.onlyMobileVisible) {
                return `height: ${this.peekHeight}; max-height: ${this.peekHeight};`;
            }
            return 'top: calc(var(--ReaderHeaderHeight, 0px) + 4px); height: calc(100vh - var(--ReaderHeaderHeight, 0px) - 8px);';
        },

        panelClasses() {
            return [
                'SidebarPanel',
                this.side === 'right' ? 'SidebarPanel--right' : 'SidebarPanel--left',
                {
                    'is-open': this.isOpen,
                    // The getter panelStyle drops the desktop height on: the rules that pin
                    // the panel to the viewport must apply whenever that height is gone.
                    'SidebarPanel--mobile': this.onlyMobileVisible,
                    'SidebarPanel--expanded': this.expanded,
                    'SidebarPanel--fullscreen': this.fullscreen,
                    'SidebarPanel--fullscreen-footer': this.fullscreen && this.fullscreenFooter,
                    'SidebarPanel--mobile-fullscreen': this.isMobileFullscreen,
                    'SidebarPanel--resizing': this.isResizing,
                },
            ];
        },

        collapseTooltip() {
            return 'Collapse panel';
        },
    },

    watch: {
        shouldTrapFocus: {
            immediate: true,
            handler(shouldTrap) {
                this.focusTrapEnabled = shouldTrap;
            },
        },
        isOpen: {
            immediate: true,
            handler(isOpen) {
                if (isOpen) this.leaveBackSteps = pushBackStep(() => this.$emit('close'));
                else this.leaveBackSteps?.();
            },
        },
    },

    beforeDestroy() {
        if (this.stopActiveResize) this.stopActiveResize();
        this.leaveBackSteps?.();
    },

    methods: {
        ...mapMutations(['setSidebarLeftWidth', 'setSidebarResizing']),
        onFocusTrapEscape() {
            this.$emit('close');
        },
        /**
         * Begins a pointer-driven resize of the left panel, clamping the width between 320px and half the
         * viewport. The width commit is throttled to one per animation frame so the viewer tracks the
         * pointer without flooding the store, and `resizing` is flagged so the EPUB viewer defers its reflow.
         * The pointer is captured on the handle so move/up events keep firing even when the cursor crosses
         * the reader iframe, guaranteeing the resize stops on pointer release.
         * @param {PointerEvent} event - The pointerdown event on the resize handle
         */
        startResize(event) {
            event.preventDefault();
            const resizeHandle = event.currentTarget;
            const { pointerId } = event;
            resizeHandle.setPointerCapture?.(pointerId);
            this.isResizing = true;
            this.setSidebarResizing(true);
            const panelRect = this.$refs.focusTrapContainer?.getBoundingClientRect();
            const maxWidth = Math.max(MIN_SIDEBAR_WIDTH, window.innerWidth / 2);

            let pendingWidth = null;
            let frameId = null;
            const commitWidth = () => {
                frameId = null;
                if (pendingWidth !== null) this.setSidebarLeftWidth(pendingWidth);
            };
            const handleMove = moveEvent => {
                const rawWidth =
                    this.side === 'left'
                        ? moveEvent.clientX - (panelRect?.left || 0)
                        : (panelRect?.right || window.innerWidth) - moveEvent.clientX;
                pendingWidth = Math.min(maxWidth, Math.max(MIN_SIDEBAR_WIDTH, rawWidth));
                if (frameId === null) frameId = requestAnimationFrame(commitWidth);
            };
            const stopResize = () => {
                if (frameId !== null) cancelAnimationFrame(frameId);
                if (pendingWidth !== null) this.setSidebarLeftWidth(pendingWidth);
                this.isResizing = false;
                this.setSidebarResizing(false);
                document.removeEventListener('pointermove', handleMove);
                document.removeEventListener('pointerup', stopResize);
                document.removeEventListener('pointercancel', stopResize);
                this.stopActiveResize = null;
                if (resizeHandle.hasPointerCapture?.(pointerId)) {
                    resizeHandle.releasePointerCapture(pointerId);
                }
            };
            this.stopActiveResize = stopResize;

            document.addEventListener('pointermove', handleMove);
            document.addEventListener('pointerup', stopResize);
            document.addEventListener('pointercancel', stopResize);
        },
        /**
         * Resets the left panel to its default width.
         */
        resetWidth() {
            this.setSidebarLeftWidth(null);
        },
    },
};
</script>

<style>
.SidebarPanel__wrapper {
    @apply relative;
}

.SidebarPanel__backdrop {
    @apply fixed left-0 z-[49] w-full cursor-default border-none bg-transparent;
    top: var(--ReaderHeaderHeight, 0px);
    height: calc(100% - var(--ReaderHeaderHeight, 0px));
}

.SidebarPanel__backdrop:focus {
    @apply outline-none;
}

.SidebarPanel__backdrop:focus-visible {
    @apply outline-none ring-2 ring-inset ring-theme-accent;
}

.SidebarPanel__backdrop--visible {
    @apply pointer-events-none bg-black/40;
}

/* Panel base styles */
.SidebarPanel {
    @apply pointer-events-none invisible fixed z-[57] flex max-w-[calc(100vw-8px)] flex-col overflow-hidden;
    background: var(--theme-background-color);
    transition:
        transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        box-shadow 0.3s ease,
        width 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        visibility 0s 0.3s;
}

/* Panel open state */
.SidebarPanel.is-open {
    @apply pointer-events-auto visible;
    transition:
        transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        box-shadow 0.3s ease,
        width 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        visibility 0s 0s;
    box-shadow: none;
}

/* Right side positioning */
.SidebarPanel--right {
    @apply right-1;
    width: var(--sidebar-panel-width-right);
    transform: translateX(calc(100% + 8px));
    border-left: 1px solid var(--theme-border-color);
}

.SidebarPanel--right.is-open {
    @apply translate-x-0;
}

/* Left side positioning */
.SidebarPanel--left {
    @apply left-1;
    width: var(--sidebar-panel-width-left);
    transform: translateX(calc(-100% - 8px));
    border-right: 1px solid var(--theme-border-color);
}

.SidebarPanel--left.is-open {
    @apply translate-x-0;
}

/* During drag-resize: drop the width transition so the panel tracks the pointer */
.SidebarPanel--resizing,
.SidebarPanel--resizing.is-open {
    transition:
        transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        box-shadow 0.3s ease;
}

/* Expanded width (480px) */
.SidebarPanel--expanded {
    @apply w-[480px];
}

/* Fullscreen mode */
.SidebarPanel--fullscreen {
    @apply !left-0 !right-0 !top-0 z-[60] !h-[100vh] w-full max-w-full rounded-none;
    border-left: none;
    border-right: none;
}

.SidebarPanel--fullscreen .SidebarPanel__header,
.SidebarPanel--fullscreen .SidebarPanel__toolbar,
.SidebarPanel--fullscreen .SidebarPanel__footer {
    @apply hidden;
}

.SidebarPanel--fullscreen.SidebarPanel--fullscreen-footer .SidebarPanel__footer {
    @apply flex;
}

.SidebarPanel--fullscreen .SidebarPanel__content {
    @apply px-8 py-6;
}

/* Collapse button */
.SidebarPanel__collapse {
    @apply flex shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-1.5;
    color: var(--theme-toolbar-icons-color);
    transition: background 0.15s;
}

.SidebarPanel__collapse:hover {
    @apply bg-black/5;
}

.SidebarPanel__collapse:focus {
    @apply outline-none;
}

.SidebarPanel__collapse:focus-visible {
    @apply outline-none ring-2 ring-theme-accent ring-offset-2;
}

/* Footer with border */
.SidebarPanel__footer {
    @apply flex shrink-0 flex-col border-t border-black/[0.06] px-4 py-3;
}

/* Drag handle - hidden on desktop */
.SidebarPanel__handle {
    @apply hidden;
}

.SidebarPanel__handle:focus {
    @apply outline-none;
}

.SidebarPanel__handle:focus-visible {
    @apply outline-none ring-2 ring-inset ring-theme-accent;
}

.SidebarPanel__handle-line {
    @apply h-1 w-9 rounded-sm opacity-40;
    background: var(--theme-toolbar-icons-color, rgba(0, 0, 0, 0.25));
}

/* ============================================
   Theme Support
   ============================================ */

/* Dark mode */
.Reader[data-theme='dark'] .SidebarPanel.is-open {
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
}

.Reader[data-theme='dark'] .SidebarPanel__collapse:hover {
    background: rgba(255, 255, 255, 0.1);
}

.Reader[data-theme='dark'] .SidebarPanel__footer {
    border-top-color: rgba(255, 255, 255, 0.08);
}

/* High Contrast Dark mode */
.Reader[data-theme='high-contrast-dark'] .SidebarPanel.is-open {
    box-shadow: none;
    border: 1px solid var(--theme-border-color);
    border-top: none;
}

.Reader[data-theme='high-contrast-dark'] .SidebarPanel__collapse:hover {
    background: rgba(255, 255, 255, 0.15);
}

.Reader[data-theme='high-contrast-dark'] .SidebarPanel__footer {
    border-top-color: rgba(255, 255, 255, 0.08);
}

/* Sepia mode */
.Reader[data-theme='sepia'] .SidebarPanel__footer {
    border-top-color: rgba(101, 67, 33, 0.08);
}

/* ============================================
   Mobile layout (SidebarPanel--mobile)
   Bottom sheet behavior
   ============================================ */

/* Show drag handle on mobile */
.SidebarPanel--mobile .SidebarPanel__handle {
    @apply flex w-full shrink-0 cursor-pointer items-center justify-center border-none bg-transparent;
    padding: 10px 0 2px;
}

/* Hide collapse button on mobile (use drag handle instead) */
.SidebarPanel--mobile .SidebarPanel__collapse {
    @apply hidden;
}

/* Mobile fullscreen shows collapse button, hides handle */
.SidebarPanel--mobile.SidebarPanel--mobile-fullscreen .SidebarPanel__handle {
    @apply hidden;
}

.SidebarPanel--mobile.SidebarPanel--mobile-fullscreen .SidebarPanel__collapse {
    @apply flex;
}

/* Panel slides from bottom */
.SidebarPanel--mobile {
    @apply left-0 right-0 max-h-[85vh] w-full max-w-full;
    top: auto !important;
    bottom: 0 !important;
    border-radius: 12px 12px 0 0;
    transform: translateY(100%);
}

/* Override side positioning for mobile */
.SidebarPanel--mobile.SidebarPanel--right,
.SidebarPanel--mobile.SidebarPanel--left {
    @apply left-0 right-0;
    transform: translateY(100%);
    border-left: none;
    border-right: none;
}

.SidebarPanel--mobile.SidebarPanel--right.is-open,
.SidebarPanel--mobile.SidebarPanel--left.is-open {
    @apply translate-y-0;
}

/* Mobile fullscreen mode */
.SidebarPanel--mobile.SidebarPanel--mobile-fullscreen {
    @apply !bottom-0 !top-0 z-[60] max-h-screen rounded-none;
    height: 100vh !important;
}

/* ============================================
   Fade transition for backdrop (Vue 2 syntax)
   ============================================ */

.transition-fade-enter-active,
.transition-fade-leave-active {
    @apply transition-opacity duration-300 ease-in-out;
}

.transition-fade-enter,
.transition-fade-leave-to {
    @apply opacity-0;
}
</style>
